import { TenantFormattingService } from '../../../core/services/tenant-formatting.service';
import { OrderChatComponent } from '../../components/order-chat/order-chat.component';
import { FileImagePreviewComponent } from '../../components/file-image-preview/file-image-preview.component';
import { isPreviewableImage } from '../../../core/utils/image-file';
import { communicationKinds } from '../../../core/models/communication.model';
import { CommunicationService } from '../../../core/services/communication.service';
import { Subscription, combineLatest } from 'rxjs';
import { AfterViewInit, Component, HostListener, OnDestroy, OnInit, inject } from '@angular/core';
import { ActivatedRoute, Router, RouterLink } from '@angular/router';
import { PaginatedList } from '../../../core/models/shared/paginated-list.model';
import { OrderModel } from '../../../core/models/order.model';
import { OrdersService } from '../../../core/services/orders.service';
import { getOrderProgressLabel, getOrderProgressStyle, OrderProgress } from '../../../core/models/shared/order-progress.enum';
import { getOrderStatusLabel, getOrderStatusStyle, OrderStatus } from '../../../core/models/shared/order-status.enum';
import { NgStyle, NgTemplateOutlet } from '@angular/common';
import { DentalArchesComponent } from '../../components/dental-arches.component/dental-arches.component';
import { ToothColors } from '../../../core/models/shared/tooth-colors.enum';
import { faChevronDown, faDownload, faPaperclip, faPenToSquare, faPlus, faTooth, faTrash } from '@fortawesome/free-solid-svg-icons';
import { FaIconComponent } from '@fortawesome/angular-fontawesome';
import { ProductModel } from '../../../core/models/product.model';
import { GroupedProduct } from '../../../core/dtos/grouped-product.dto';
import { LoadingSpinnerComponent } from '../../components/loading-spinner.component/loading-spinner.component';
import { FilesService } from '../../../core/services/files.service';
import { FileModel } from '../../../core/models/file.model';
import { CreateFilesComponent } from '../files/create-files/create-files.component';
import { OrderSortOption } from '../../../core/models/shared/order-sort.model';
import { MatPaginatorModule, PageEvent } from '@angular/material/paginator';
import { MatTooltipModule } from '@angular/material/tooltip';
import { SearchInputComponent } from '../../components/search-input/search-input.component';
import { SortDropdownComponent } from '../../components/sort-dropdown/sort-dropdown.component';
import { SortOptionBase } from '../../../core/models/shared/sort-option-base.model';
import { AppDatePipe } from '../../pipes/app-date.pipe';
import { NoItemsFoundComponent } from '../../components/no-items-found/no-items-found.component';
import { finalize } from 'rxjs';
import { ErrorAlertComponent } from '../../components/error-alert.component/error-alert.component';
import { getApiErrorMessage } from '../../../core/utils/api-error-message';
type OrderTab = 'details' | 'communication' | 'files';
interface FilterOption {
  label: string;
  value: string;
  selected: boolean;
}
const ORDER_SORT_OPTIONS: OrderSortOption[] = [
  { label: 'По статус растечки', sortBy: 'OrderProgress', sortDirection: 'Ascending' },
  { label: 'По статус опаѓачки', sortBy: 'OrderProgress', sortDirection: 'Descending' },
  { label: 'Најнови', sortBy: 'DateCreated', sortDirection: 'Descending' },
  { label: 'Најстари', sortBy: 'DateCreated', sortDirection: 'Ascending' }
];
export function getPageAfterDeletion(currentPage: number, currentItemCount: number): number {
  return currentPage > 1 && currentItemCount <= 1 ? currentPage - 1 : currentPage;
}
@Component({
  selector: 'app-orders',
  imports: [
    OrderChatComponent,
    FileImagePreviewComponent,
    NgStyle,
    NgTemplateOutlet,
    DentalArchesComponent,
    FaIconComponent,
    LoadingSpinnerComponent,
    CreateFilesComponent,
    MatPaginatorModule,
    MatTooltipModule,
    SearchInputComponent,
    SortDropdownComponent,
    AppDatePipe,
    NoItemsFoundComponent,
    RouterLink,
    ErrorAlertComponent
  ],
  templateUrl: './orders.component.html',
  styleUrl: './orders.component.css'
})
export class OrdersComponent implements OnInit, AfterViewInit, OnDestroy {
  private readonly router = inject(Router);
  readonly formatting = inject(TenantFormattingService);
  readonly isPreviewableImage = isPreviewableImage;
  readonly orderTabs: {
    id: OrderTab;
    label: string;
  }[] = [
      { id: 'details', label: 'Детали' },
      { id: 'communication', label: 'Пораки' },
      { id: 'files', label: 'Датотеки' }
    ];
  private selectedOrderTabs: Record<string, OrderTab> = {};
  private communicationSubscription?: Subscription;
  private routeSubscription?: Subscription;
  private listRequest?: Subscription;
  private readonly detailRequests = new Map<string, Subscription>();
  private navigationRequest?: Subscription;
  orders?: PaginatedList<OrderModel>;
  loading = false;
  error = '';
  expandedOrderIds: string[] = [];
  private uploadModalTriggers: Record<string, HTMLElement> = {};
  pendingDeleteOrder: OrderModel | null = null;
  deletingOrder = false;
  downloadingFileIds = new Set<string>();
  private deleteModalTrigger: HTMLElement | null = null;
  private deleteModalElement: HTMLElement | null = null;
  private readonly handleDeleteModalHidden = (): void => this.cleanupDeleteModal();
  private readonly handleDeleteModalHide = (event: Event): void => {
    if (this.deletingOrder)
      event.preventDefault();
  };
  ToothColors = ToothColors;
  OrderStatus = OrderStatus;
  faTooth = faTooth;
  faPlus = faPlus;
  faPaperclip = faPaperclip;
  faChevronDown = faChevronDown;
  faPenToSquare = faPenToSquare;
  faTrash = faTrash;
  readonly getProgressStyle = getOrderProgressStyle;
  readonly getStatusStyle = getOrderStatusStyle;
  searchText: string = '';
  filtersOpen = false;
  sortOptions = ORDER_SORT_OPTIONS;
  selectedSort = ORDER_SORT_OPTIONS[0];
  pageSize = 10;
  filterOptions: FilterOption[] = Object.keys(OrderProgress)
    .filter((key) => isNaN(Number(key)))
    .map(key => {
      const progress = OrderProgress[key as keyof typeof OrderProgress] as OrderProgress;
      return {
        label: getOrderProgressLabel(progress),
        value: key,
        selected: true
      };
    });
  constructor(private orderService: OrdersService, private filesService: FilesService, private route: ActivatedRoute, public communication: CommunicationService) { }
  ngOnInit(): void {
    this.communicationSubscription = this.communication.changes.subscribe(change => {
      if (change.kind !== communicationKinds.read)
        this.refreshOpenOrders(change.orderId, change.kind === communicationKinds.resync);
    });
    this.routeSubscription = combineLatest([this.route.paramMap, this.route.queryParamMap]).subscribe(([routeParams, params]) => {
      this.searchText = params.get('search') ?? '';
      const id = routeParams.get('id') ?? params.get('expandId');
      this.navigationRequest?.unsubscribe();
      this.detailRequests.forEach(request => request.unsubscribe());
      this.detailRequests.clear();
      this.expandedOrderIds = id ? [id] : [];
      if (id) {
        const requestedTab = params.get('tab') ?? (params.get('chat') === '1' ? 'communication' : 'details');
        const tab = requestedTab === 'communication' || requestedTab === 'files' ? requestedTab : 'details';
        this.selectOrderTab(id, tab);
        // Older links may contain only an ID. Resolve the search before opening the normal list.
        if (routeParams.has('id') || !this.searchText.trim()) {
          this.openLinkedOrderInList(id, tab);
          return;
        }
      }
      this.loadOrders();
    });
  }
  ngAfterViewInit(): void {
    this.deleteModalElement = document.getElementById('deleteOrderModal');
    this.deleteModalElement?.addEventListener('hidden.bs.modal', this.handleDeleteModalHidden);
    this.deleteModalElement?.addEventListener('hide.bs.modal', this.handleDeleteModalHide);
  }
  ngOnDestroy(): void {
    this.communicationSubscription?.unsubscribe();
    this.routeSubscription?.unsubscribe();
    this.navigationRequest?.unsubscribe();
    this.listRequest?.unsubscribe();
    this.detailRequests.forEach(request => request.unsubscribe());
    this.detailRequests.clear();
    this.deleteModalElement?.removeEventListener('hidden.bs.modal', this.handleDeleteModalHidden);
    this.deleteModalElement?.removeEventListener('hide.bs.modal', this.handleDeleteModalHide);
  }
  loadOrders(page: number = 1, pageSize: number = this.pageSize): void {
    this.listRequest?.unsubscribe();
    this.loading = true;
    this.error = '';
    this.pageSize = pageSize;
    let selectedFilters = this.filterOptions
      .filter(option => option.selected)
      .map(option => option.value);
    this.listRequest = this.orderService.getOrders(page, this.searchText, selectedFilters, this.pageSize, this.selectedSort.sortBy, this.selectedSort.sortDirection).subscribe({
      next: (data) => {
        this.orders = data;
        this.loading = false;
      },
      error: (err) => {
        this.error = getApiErrorMessage(err, 'Налозите не може да се вчитаат.');
        console.error(err);
        this.loading = false;
      }
    });
  }
  toggleExpand(orderId: string): void {
    const index = this.expandedOrderIds.indexOf(orderId);
    if (index > -1) {
      this.expandedOrderIds.splice(index, 1);
      this.detailRequests.get(orderId)?.unsubscribe();
    }
    else {
      this.expandedOrderIds.push(orderId); // add
    }
  }
  canModifyOrder(order: OrderModel): boolean {
    return order.orderStatus === OrderStatus.Requested || order.orderStatus === OrderStatus.Rejected;
  }
  openDeleteOrderModal(event: MouseEvent, order: OrderModel): void {
    event.stopPropagation();
    if (!this.canModifyOrder(order))
      return;
    this.pendingDeleteOrder = order;
    this.deleteModalTrigger = event.currentTarget as HTMLElement | null;
    this.showModal('deleteOrderModal');
  }
  confirmDeleteOrder(): void {
    const order = this.pendingDeleteOrder;
    if (this.deletingOrder || !order || !this.canModifyOrder(order))
      return;
    this.deletingOrder = true;
    this.orderService.deleteOrder(order.id).subscribe({
      next: () => {
        this.deletingOrder = false;
        const targetPage = this.orders
          ? getPageAfterDeletion(this.orders.currentPage, this.orders.items.length)
          : 1;
        this.expandedOrderIds = this.expandedOrderIds.filter(id => id !== order.id);
        this.hideDeleteOrderModal();
        this.loadOrders(targetPage);
      },
      error: err => {
        this.deletingOrder = false;
        console.error(err);
        this.error = getApiErrorMessage(err, 'Налогот не може да се избрише. Може да се бришат само испратени или одбиени налози.');
      }
    });
  }
  hideDeleteOrderModal(): void {
    const modalEl = document.getElementById('deleteOrderModal');
    const bootstrap = (window as any).bootstrap;
    if (!modalEl || !bootstrap?.Modal) {
      this.cleanupDeleteModal();
      return;
    }
    const activeElement = document.activeElement as HTMLElement | null;
    if (activeElement && modalEl.contains(activeElement))
      activeElement.blur();
    const instance = bootstrap.Modal.getInstance(modalEl) ?? new bootstrap.Modal(modalEl);
    instance.hide();
  }
  private cleanupDeleteModal(): void {
    this.pendingDeleteOrder = null;
    if (this.deleteModalTrigger?.isConnected) {
      this.deleteModalTrigger.focus({ preventScroll: true });
    }
    this.deleteModalTrigger = null;
  }
  private showModal(id: string): void {
    const modalEl = document.getElementById(id);
    const bootstrap = (window as any).bootstrap;
    if (!modalEl || !bootstrap?.Modal)
      return;
    const instance = bootstrap.Modal.getInstance(modalEl) ?? new bootstrap.Modal(modalEl);
    instance.show();
  }
  openOrderUploadModal(event: MouseEvent, orderId: string): void {
    event.stopPropagation();
    const trigger = event.currentTarget as HTMLElement | null;
    if (trigger) {
      this.uploadModalTriggers[orderId] = trigger;
    }
    const modalEl = document.getElementById(`orderUploadFileModal-${orderId}`);
    const bootstrap = (window as any).bootstrap;
    if (!modalEl || !bootstrap?.Modal)
      return;
    const instance = bootstrap.Modal.getInstance(modalEl) ?? new bootstrap.Modal(modalEl);
    instance.show();
  }
  downloadFile(file: FileModel): void {
    if (!file.id || this.downloadingFileIds.has(file.id))
      return;
    this.error = '';
    this.downloadingFileIds.add(file.id);
    this.filesService.requestDownloadTicket(file.id).pipe(finalize(() => {
      this.downloadingFileIds.delete(file.id);
    })).subscribe({
      next: ({ ticket }) => this.filesService.startBrowserDownload(ticket),
      error: (err) => {
        console.error(err);
        this.error = getApiErrorMessage(err, 'Датотеката не може да се преземе.');
      }
    });
  }
  isDownloading(fileId: string): boolean {
    return this.downloadingFileIds.has(fileId);
  }
  formatBytes(bytes?: number): string {
    if (!bytes || bytes <= 0)
      return '0 MB';
    const mb = bytes / 1024 / 1024;
    return `${mb.toFixed(2)} MB`;
  }
  handleOrderFileCreated(orderId: string, file: FileModel): void {
    const order = this.orders?.items.find(o => o.id === orderId);
    if (order) {
      order.files = [file, ...(order.files ?? []).filter(existing => existing.id !== file.id)];
    }
    this.hideOrderUploadModal(orderId);
  }
  hideOrderUploadModal(orderId: string): void {
    const modalEl = document.getElementById(`orderUploadFileModal-${orderId}`);
    const bootstrap = (window as any).bootstrap;
    if (!modalEl || !bootstrap?.Modal)
      return;
    const trigger = this.uploadModalTriggers[orderId];
    if (trigger?.isConnected) {
      trigger.focus({ preventScroll: true });
    }
    else {
      const activeElement = document.activeElement as HTMLElement | null;
      if (activeElement && modalEl.contains(activeElement)) {
        activeElement.blur();
      }
    }
    const instance = bootstrap.Modal.getInstance(modalEl) ?? new bootstrap.Modal(modalEl);
    instance.hide();
  }
  getProgressLabel(progress: OrderProgress): string {
    return getOrderProgressLabel(progress);
  }
  getStatusLabel(status: OrderStatus): string {
    return getOrderStatusLabel(status);
  }
  groupProductsByName(products: ProductModel[]): GroupedProduct[] {
    const map = new Map<string, GroupedProduct>();
    products.forEach(p => {
      if (map.has(p.name)) {
        const existing = map.get(p.name)!;
        existing.quantity += p.quantity;
      }
      else {
        map.set(p.name, { name: p.name, quantity: p.quantity, color: p.color ?? '#2e8bc0' });
      }
    });
    return Array.from(map.values());
  }
  // Helper to see how many are selected
  get selectedCount(): number {
    return this.filterOptions.filter(f => f.selected).length;
  }
  toggleFilters(event: MouseEvent) {
    event.stopPropagation();
    this.filtersOpen = !this.filtersOpen;
  }
  chooseFilter(option: FilterOption, event: MouseEvent) {
    event.stopPropagation();
    option.selected = !option.selected;
    this.loadOrders();
  }
  @HostListener('document:click')
  closeFilters() {
    this.filtersOpen = false;
  }
  onSort(option: SortOptionBase) {
    this.selectedSort = option as OrderSortOption;
    this.loadOrders();
  }
  onPageChange(event: PageEvent): void {
    this.loadOrders(event.pageIndex + 1, event.pageSize);
  }
  selectedOrderTab(orderId: string): OrderTab {
    return this.selectedOrderTabs[orderId] ?? 'details';
  }
  selectOrderTab(orderId: string, tab: OrderTab): void {
    this.selectedOrderTabs[orderId] = tab;
  }
  onOrderTabKey(event: KeyboardEvent, orderId: string): void {
    if (!['ArrowLeft', 'ArrowRight', 'Home', 'End'].includes(event.key))
      return;
    event.preventDefault();
    const tabs = this.orderTabs;
    const current = tabs.findIndex(tab => tab.id === this.selectedOrderTab(orderId));
    const next = event.key === 'Home' ? 0 : event.key === 'End' ? tabs.length - 1
      : (current + (event.key === 'ArrowRight' ? 1 : -1) + tabs.length) % tabs.length;
    const tab = tabs[next].id;
    this.selectOrderTab(orderId, tab);
    document.getElementById(`${tab}-tab-${orderId}`)?.focus();
  }
  private refreshOpenOrders(orderId?: string, resync = false): void {
    for (const id of this.expandedOrderIds) {
      if (orderId && id !== orderId)
        continue;
      if (resync && this.detailRequests.get(id)?.closed === false)
        continue;
      this.fetchOrderDetails(id);
    }
  }
  private openLinkedOrderInList(id: string, tab: OrderTab): void {
    this.listRequest?.unsubscribe();
    this.orders = undefined;
    this.loading = true;
    this.error = '';
    this.navigationRequest = this.orderService.getOrderDetails(id).subscribe({
      next: order => {
        void this.router.navigate(['/orders'], {
          queryParams: { search: order.formattedOrderNumber, expandId: id, tab },
          replaceUrl: true
        });
      },
      error: err => {
        this.loading = false;
        this.error = getApiErrorMessage(err, 'Налогот не е достапен.');
      }
    });
  }
  private fetchOrderDetails(id: string): void {
    this.detailRequests.get(id)?.unsubscribe();
    const request = this.orderService.getOrderDetails(id).subscribe({
      next: order => {
        if (this.orders)
          this.orders.items = this.orders.items.map(item => item.id === id ? order : item);
      },
      error: err => {
        if (err.status === 403 || err.status === 404) {
          this.expandedOrderIds = this.expandedOrderIds.filter(value => value !== id);
          if (this.orders?.items.some(item => item.id === id))
            this.loadOrders(this.orders.currentPage);
        }
        this.error = getApiErrorMessage(err, 'Налогот не е достапен.');
      }
    });
    this.detailRequests.set(id, request);
  }
  protected readonly faDownload = faDownload;
}
